import gql from 'graphql-tag';
import { provideApolloClient } from '@vue/apollo-composable';
import { reactive, ref } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import { getPhoneNumber } from './usePhoneTypes';
import apolloClient from './useApolloClient';
import useAlerts from './useAlerts';
import useStatesAndCountries from './useStatesAndCountries';
import useUsersQuery from './useUsersQuery';
const { addAlert } = useAlerts();
const { decryptSymbols, encryptSymbols } = useDecryptSymbols();
const { stateAbbreviations } = useStatesAndCountries();
const { getUserById } = useUsersQuery();

const reportData = ref([]);
const loading = ref(false);

const stateList = stateAbbreviations.value;

const columnsList = [
  { name: 'COMPANYNAME', title: 'Company Name', flexWidth: 3, order: 1 },
  { name: 'CONTACTNAME', title: 'Contact Name', flexWidth: 3, order: 2 },
  { name: 'COMPANYPHONE', title: 'Company Phone', flexWidth: 3, order: 3 },
  { name: 'CELLDIRECT', title: 'Cell / Direct', flexWidth: 3, order: 4 },
  { name: 'EMAIL', title: 'Email', flexWidth: 3, order: 5 },
  { name: 'CUSTOMERDETAILS', title: 'Customer Details', flexWidth: 3, order: 6 },
  { name: 'INSIDESALESPERSON', title: 'Inside Salesperson', flexWidth: 3, order: 7 },
  { name: 'OUTSIDESALESPERSON', title: 'Outside Salesperson', flexWidth: 3, order: 8 },
  { name: 'CERTIFIEDCONTRACTOR', title: 'Certified Contractor', flexWidth: 3, order: 9 },
  { name: 'CITY', title: 'City', flexWidth: 2, order: 10 },
  { name: 'STATE', title: 'State', flexWidth: 2, order: 11 },
  { name: 'RECENTDIARYNOTES', title: 'Recent Diary Notes', flexWidth: 7, order: 12 },
];

const filters = reactive({
  columns: ['COMPANYNAME', 'CONTACTNAME', 'COMPANYPHONE', 'CELLDIRECT', 'RECENTDIARYNOTES'],
  insideSalespeople: [],
  outsideSalespeople: [],
  customerDetails: [],
  states: [],
  certifiedContractorsOnly: false,
  salesContactsOnly: true,
  diaryNotesDateFrom: '',
  diaryNotesDateTo: '',
});

const defaultFilters = JSON.parse(JSON.stringify(filters));

const resetFilters = () => {
  for (const key in filters) {
    filters[key] = defaultFilters[key];
  }
};

const GET_CUSTOMER_REPORT = gql`
  query CustomerReport($filters: CustomerReportFilters) {
    customerReport(filters: $filters) {
      companies {
        NAME
        COMPANYID
        INSIDESALES
        OUTSIDESALES
        CUSTOMERDETAILS
        CERTIFIEDCONTRACTOR
        contacts {
          FIRSTNAME
          LASTNAME
          CONTACTID
          INSIDESALES
          OUTSIDESALES
          CUSTOMERDETAILS
          SALESCONTACT
          address {
            CITY
            STATE
          }
          phones {
            NUMBERDISPLAY
            TYPEID
            EXT
          }
          diarys {
            CREATEDATE
            ASSIGNEDID
            TYPE
          }
          email {
            ADDRESS
          }
        }
        address {
          CITY
          STATE
        }
        phones {
          NUMBERDISPLAY
          TYPEID
          EXT
        }
        diarys {
          CREATEDATE
          ASSIGNEDID
          TYPE
        }
      }
      contacts {
        FIRSTNAME
        LASTNAME
        CONTACTID
        INSIDESALES
        OUTSIDESALES
        CUSTOMERDETAILS
        SALESCONTACT
        company {
          NAME
          COMPANYID
          phones {
            NUMBERDISPLAY
            TYPEID
            EXT
          }
        }
        address {
          CITY
          STATE
        }
        phones {
          NUMBERDISPLAY
          TYPEID
          EXT
        }
        diarys {
          CREATEDATE
          ASSIGNEDID
          TYPE
        }
        email {
          ADDRESS
        }
      }
    }
  }
`;

const validateFilters = () => {
  if (!filters.columns.length) {
    addAlert('Please select at least one column', 'high', 5);
    return false;
  }
  if (!filters.insideSalespeople.length && !filters.outsideSalespeople.length && !filters.customerDetails.length && !filters.states.length) {
    addAlert('Please select at least one filter', 'high', 5);
    return false;
  }
  return true;
};

const useCustomerReportQuery = function () {
  provideApolloClient(apolloClient);

  const runReport = async () => {
    if (!validateFilters()) return;

    loading.value = true;
    try {
      const filterParams = {
        insideSalespeople: [...filters.insideSalespeople],
        outsideSalespeople: [...filters.outsideSalespeople],
        customerDetails: [...encryptSymbols(filters.customerDetails)],
        states: [...encryptSymbols(filters.states)],
        certifiedContractorsOnly: filters.certifiedContractorsOnly,
        salesContactsOnly: filters.salesContactsOnly,
      };

      const { data } = await apolloClient.query({
        query: GET_CUSTOMER_REPORT,
        variables: { filters: filterParams },
        fetchPolicy: 'network-only',
      });

      if (data?.customerReport) {
        const decrypted = decryptSymbols(data.customerReport);
        formatReportData(decrypted);
      }
    } catch (err) {
      console.error('Error fetching customer report:', err);
      addAlert('Error fetching customer report', 'high', 4);
    } finally {
      loading.value = false;
    }
  };

  return {
    reportData,
    loading,
    filters,
    runReport,
    columnsList,
    stateList,
    validateFilters,
    resetFilters,
  };
};

function getDisplayNameByUserId(userId) {
  if (!userId) return '';
  const user = getUserById(userId);
  return user ? user.DISPLAYNAME : '';
}

function formatReportData(data) {
  const formattedData = [];

  data.companies.forEach((company) => {
    if (company.contacts.length == 0) {
      formattedData.push({
        COMPANYID: company.COMPANYID,
        CONTACTID: null,
        COMPANYNAME: company.NAME,
        CONTACTNAME: '',
        COMPANYPHONE: getPhoneNumber(company.phones, 'main') || getPhoneNumber(company.phones, 'tollFree'),
        CELLDIRECT: '',
        EMAIL: '',
        CUSTOMERDETAILS: Array.isArray(company.CUSTOMERDETAILS) ? company.CUSTOMERDETAILS.join(', ') : company.CUSTOMERDETAILS || '',
        INSIDESALESPERSON: getDisplayNameByUserId(company.INSIDESALES),
        OUTSIDESALESPERSON: getDisplayNameByUserId(company.OUTSIDESALES),
        CERTIFIEDCONTRACTOR: company.CERTIFIEDCONTRACTOR ? 'Yes' : '',
        CITY: company.address?.CITY || '',
        STATE: company.address?.STATE || '',
        RECENTDIARYNOTES: formatDiaryNotes(company.diarys),
      });
    } else {
      company.contacts.forEach((contact) => {
        formattedData.push({
          COMPANYID: company.COMPANYID,
          CONTACTID: contact.CONTACTID,
          COMPANYNAME: company.NAME,
          CONTACTNAME: (contact.FIRSTNAME || '') + ' ' + (contact.LASTNAME || ''),
          COMPANYPHONE: getPhoneNumber(company.phones, 'main') || getPhoneNumber(company.phones, 'tollFree'),
          CELLDIRECT: getPhoneNumber(contact.phones, 'direct') || getPhoneNumber(contact.phones, 'mobile'),
          EMAIL: contact.email?.ADDRESS || '',
          CUSTOMERDETAILS: Array.isArray(company.CUSTOMERDETAILS) ? company.CUSTOMERDETAILS.join(', ') : company.CUSTOMERDETAILS || '',
          INSIDESALESPERSON: getDisplayNameByUserId(company.INSIDESALES),
          OUTSIDESALESPERSON: getDisplayNameByUserId(company.OUTSIDESALES),
          CERTIFIEDCONTRACTOR: company.CERTIFIEDCONTRACTOR ? 'Yes' : '',
          CITY: company.address?.CITY || '',
          STATE: company.address?.STATE || '',
          RECENTDIARYNOTES: formatDiaryNotes(contact.diarys),
        });
      });
    }
  });

  data.contacts.forEach((contact) => {
    formattedData.push({
      COMPANYID: contact.company?.COMPANYID || '',
      CONTACTID: contact.CONTACTID,
      COMPANYNAME: contact.company?.NAME || '',
      CONTACTNAME: (contact.FIRSTNAME || '') + ' ' + (contact.LASTNAME || ''),
      COMPANYPHONE: getPhoneNumber(contact.company?.phones, 'main') || getPhoneNumber(contact.company?.phones, 'tollFree'),
      CELLDIRECT: getPhoneNumber(contact.phones, 'direct') || getPhoneNumber(contact.phones, 'mobile'),
      EMAIL: contact.email?.ADDRESS || '',
      CUSTOMERDETAILS: Array.isArray(contact.CUSTOMERDETAILS) ? contact.CUSTOMERDETAILS.join(', ') : contact.CUSTOMERDETAILS || '',
      INSIDESALESPERSON: getDisplayNameByUserId(contact.INSIDESALES),
      OUTSIDESALESPERSON: getDisplayNameByUserId(contact.OUTSIDESALES),
      CERTIFIEDCONTRACTOR: '',
      CITY: contact.address?.CITY || '',
      STATE: contact.address?.STATE || '',
      RECENTDIARYNOTES: formatDiaryNotes(contact.diarys),
    });
  });

  reportData.value = formattedData;
}

function formatDiaryNotes(diaryNotes) {
  if (diaryNotes.length == 0) {
    return '';
  }

  // Convert diary notes dates to timestamps
  diaryNotes.forEach((note) => {
    note.CREATEDATE = parseInt(note.CREATEDATE) || null;
    note.formattedDate = new Date(note.CREATEDATE).toLocaleDateString();
  });

  // Filter notes by date range if dates are specified
  let filteredNotes = [...diaryNotes];
  if (filters.diaryNotesDateFrom || filters.diaryNotesDateTo) {
    const fromDate = filters.diaryNotesDateFrom ? new Date(filters.diaryNotesDateFrom).getTime() : 0;
    const toDate = filters.diaryNotesDateTo ? new Date(filters.diaryNotesDateTo).getTime() : Date.now();

    filteredNotes = diaryNotes.filter((note) => {
      const noteDate = note.CREATEDATE;
      return noteDate >= fromDate && noteDate <= toDate;
    });
  }

  const sortedDiaryNotes = filteredNotes.sort((a, b) => b.CREATEDATE - a.CREATEDATE);
  const formattedDiaryNotes = sortedDiaryNotes
    .slice(0, 3)
    .map((note) => `${note.formattedDate} - ${getDisplayNameByUserId(note.ASSIGNEDID)} - ${note.TYPE}`)
    .join(' | ');

  return formattedDiaryNotes;
}

export default useCustomerReportQuery;
